import React, { useRef } from "react";
import Cookies from "js-cookie";
import {
  HiOutlineArrowLeft,
  HiOutlineCollection,
  HiOutlineCash,
  HiOutlinePrinter,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import PDF from "../CommonPDF/PDF";
import { formatMoney } from "../../App";
import dateFormat from "dateformat";

const headRows = [
  { id: "sno", label: "S.No", typeDt: "sno" },
  { id: "presid", label: "Prescription ID" },
  { id: "customer", label: "Name", typeDt: "str" },
  { id: "customer_phno", label: "Member Phone no" },
  { id: "doctorName", label: "Doctor", typeDt: "str" },
  { id: "prescribed_on", label: "Prescribed on", typeDt: "date" },
  { id: "status", label: "Status", typeDt: "str" },
  { id: "billed_amount", label: "Rate", typeDt: "amt", type: "amt" },
];

const MedicineModal = ({ onClose, data, pdfData, tittle, downloadInvoice }) => {
  const pdfRef = useRef(null);

  let medicineList = [];
  try {
    medicineList = typeof data === "string" ? JSON.parse(data) : data || [];
  } catch (e) {
    medicineList = [];
  }

  const patientName = pdfData?.customer || pdfData?.name || "Patient";
  const patientPhone = pdfData?.customer_phno || pdfData?.phone_no || "-";
  const patientGender = pdfData?.gender || "-";
  const rawAge = pdfData?.age || pdfData?.patient_age;
  const patientAge = rawAge
    ? rawAge
    : pdfData?.customer_dob
    ? Math.floor((new Date() - new Date(pdfData.customer_dob)) / (365.25 * 24 * 3600 * 1000))
    : null;
  const doctorName = pdfData?.doctor_name || pdfData?.doctorName ? `Dr. ${pdfData.doctor_name || pdfData.doctorName}` : "Clinic Doctor";
  const clinicName = pdfData?.clinic_name || pdfData?.clinicName || localStorage.getItem("clinic_name") || Cookies.get("clinic_name") || "";
  const clinicId = pdfData?.clinic_id || pdfData?.clinicId || "";
  const prescriptionDate = pdfData?.prescript_date || pdfData?.created_on || new Date();
  const invoiceNo = pdfData?.custom_order_id || pdfData?.prescription_id ? `#${pdfData.custom_order_id || pdfData.prescription_id}` : "#PHAR";
  const isPaid = pdfData?.payment_status === 1 || pdfData?.status_id === 8 || pdfData?.status === "Delivered";

  const timingLabels = ["Morn", "Noon", "Eve", "Night"];

  const totalAmount = medicineList.reduce((sum, item) => {
    const lineTotal =
      parseFloat(item.price) ||
      parseFloat(item.amount) ||
      (parseFloat(item.per_tablet_price) || parseFloat(item.unit_rate) || 0) *
        (Number(item.totalmedicine) || Number(item.quantity) || 1);
    return sum + lineTotal;
  }, 0);

  const handlePrint = () => {
    if (downloadInvoice) {
      downloadInvoice(pdfData);
    } else if (pdfRef.current && pdfRef.current.generatePdf) {
      pdfRef.current.generatePdf();
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Top Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="w-12 h-12 rounded-2xl bg-teal-50 hover:bg-teal-600 text-teal-700 hover:text-white flex items-center justify-center transition-all duration-300 shadow-xs active:scale-95 group cursor-pointer"
            title="Back to Today's Prescriptions"
          >
            <HiOutlineArrowLeft size={22} className="group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                {patientName}'s <span className="text-teal-600">Medicine List</span>
              </h1>
              <span className="px-3.5 py-1 rounded-full bg-teal-50 text-teal-700 text-xs sm:text-sm font-black border border-teal-100 font-mono">
                {invoiceNo}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
              Viewing complete prescription details, dosage timings, and medicine charges
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handlePrint}
          className="flex items-center gap-2.5 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm rounded-2xl transition-all shadow-md shadow-teal-600/20 active:scale-95 cursor-pointer self-start sm:self-auto"
        >
          <HiOutlinePrinter size={20} />
          <span>Print / Download Invoice</span>
        </button>
      </div>

      {/* Patient & Prescribing Summary Card */}
      <div className="p-6 rounded-3xl bg-white border border-gray-100 shadow-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-slate-400">Patient Information</p>
          <p className="text-lg font-black text-slate-900 mt-1">{patientName}</p>
          <p className="text-xs text-slate-500 font-medium">
            {patientAge ? `${patientAge} yrs` : ""}{patientAge && patientGender !== "-" ? " • " : ""}{patientGender !== "-" ? patientGender : ""}
          </p>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-slate-400">Phone Number</p>
          <p className="text-base font-bold text-slate-800 font-mono mt-1">{patientPhone}</p>
          <p className="text-xs text-slate-400">Primary Contact</p>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-slate-400">Prescribing Doctor</p>
          <p className="text-base font-bold text-teal-700 mt-1">{doctorName}</p>
          <p className="text-xs text-slate-400">{dateFormat(prescriptionDate, "dd-mmm-yyyy")}</p>
        </div>

        {clinicName && (
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-400">Clinic</p>
            <p className="text-base font-bold text-slate-800 mt-1">{clinicName}</p>
            {clinicId ? <p className="text-xs text-slate-400">Clinic ID: {clinicId}</p> : null}
          </div>
        )}

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-slate-400">Total Billed</p>
          <p className="text-xl font-black text-teal-800 mt-1">
            {formatMoney(totalAmount)} <span className="text-xs font-bold text-teal-600">₹</span>
          </p>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold mt-1 ${
              isPaid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}
          >
            {isPaid ? "Paid & Dispensed" : "Payment Pending"}
          </span>
        </div>
      </div>

      {/* Prescribed Medicine Cards Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-gray-800 tracking-tight">
            Prescribed Items ({medicineList.length})
          </h2>
        </div>

        {medicineList.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-3xl border border-gray-100 text-gray-400">
            No medicine details recorded for this prescription.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {medicineList.map((val, index) => {
              const days = Number(val.days) || 1;
              const counts =
                Array.isArray(val.counts) && val.counts.length >= 4
                  ? val.counts
                  : [
                      Number(val.morning) || 0,
                      Number(val.afternoon) || 0,
                      Number(val.evening) || 0,
                      Number(val.night) || 0,
                    ];
              const dailyCount = counts.reduce((a, b) => a + (Number(b) || 0), 0) || 1;
              const totalQty = Number(val.totalmedicine) || Number(val.quantity) || days * dailyCount;
              const unitRate =
                parseFloat(val.per_tablet_price) ||
                parseFloat(val.unit_rate) ||
                parseFloat(val.unitRate) ||
                0;
              const lineTotal =
                parseFloat(val.price) ||
                parseFloat(val.amount) ||
                unitRate * totalQty;

              return (
                <div
                  key={index}
                  className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start gap-3.5 mb-4">
                      <div className="w-10 h-10 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                        <HiOutlineCollection size={20} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-black text-teal-600 uppercase tracking-widest block">
                          Medicine #{index + 1}
                        </span>
                        <h3 className="text-base font-black text-gray-900 truncate">
                          {val.name || val.medicine_name || val.label}
                        </h3>
                        {val.dosage && (
                          <span className="inline-block text-xs font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md mt-0.5">
                            {val.dosage}
                          </span>
                        )}
                        {val.instruction && (
                          <p className="text-xs text-gray-500 italic mt-1">{val.instruction}</p>
                        )}
                      </div>
                    </div>

                    {/* Schedule Counts */}
                    <div className="grid grid-cols-4 gap-2 mb-4 bg-slate-50 p-2.5 rounded-2xl border border-slate-100">
                      {timingLabels.map((time, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">
                            {time}
                          </p>
                          <div
                            className={`w-full py-1 rounded-xl text-center font-black text-xs ${
                              counts[i] > 0
                                ? "bg-teal-600 text-white shadow-xs"
                                : "bg-white text-slate-300 border border-slate-200"
                            }`}
                          >
                            {counts[i] || 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-slate-900 text-white px-2.5 py-1 rounded-lg text-xs font-black">
                        {days} {days === 1 ? "Day" : "Days"}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-lg text-xs font-black uppercase ${
                          val.foodtype === 0
                            ? "bg-teal-100 text-teal-800 border border-teal-200"
                            : "bg-blue-100 text-blue-800 border border-blue-200"
                        }`}
                      >
                        {val.foodtype === 0 ? "Before Food" : "After Food"}
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-2.5 py-1 rounded-lg text-xs font-black">
                        Qty: {totalQty}
                      </span>
                    </div>

                    <div className="text-right">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Amount</p>
                      <p className="text-base font-black text-teal-800 font-mono">
                        {formatMoney(lineTotal)} <span className="text-xs">₹</span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Sticky Action Bar */}
      <div className="bg-slate-900 text-white p-6 rounded-3xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400">
            <HiOutlineCash size={28} />
          </div>
          <div>
            <p className="text-teal-400 text-xs font-black uppercase tracking-wider">Total Prescription Amount Due</p>
            <h3 className="text-3xl font-black text-white font-mono tracking-tight mt-0.5">
              {formatMoney(totalAmount)} <span className="text-lg font-bold text-teal-400">₹</span>
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl transition-all cursor-pointer"
          >
            Back to List
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="px-8 py-3.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-black text-sm rounded-2xl transition-all shadow-lg shadow-teal-500/20 active:scale-95 flex items-center gap-2.5 cursor-pointer"
          >
            <HiOutlinePrinter size={20} />
            <span>Print Invoice</span>
          </button>
        </div>
      </div>

      {/* Hidden PDF Component for Generating Print */}
      <PDF
        ref={pdfRef}
        pdfData={pdfData}
        weekMonthYearData={medicineList}
        headRows={headRows}
        filename={tittle}
        headingTitle={tittle}
      />
    </div>
  );
};

export default MedicineModal;

